/**
 * AppShowcaseSection — the SaaS product "Quítamelo" pitched on the landing.
 * Interactive iPhone-style carousel + features + pricing + waitlist CTA.
 */
import React, { useState, useCallback } from 'react';
import {
  Sparkles, Camera, MessageCircle,
  BarChart3, Trophy, Target, ShieldCheck, Landmark, Bell,
  FileText, Link2, Zap, CheckCircle2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { analytics } from '../../services/analytics';
import { API } from '../../services/api';
import axios from 'axios';
import './AppShowcaseSection.css';

const HERO_SCREEN = {
  src: 'https://customer-assets.emergentagent.com/job_ahorro-constante/artifacts/4ckak74w_IMG_9076.jpeg',
  alt: 'Dashboard Quítamelo — Racha, Nivel Sobreviviendo y Consejo del día'
};

const APP_HIGHLIGHTS = [
  { icon: MessageCircle, text: 'Asistente Quitamelo con IA que analiza cada gasto contigo' },
  { icon: Camera, text: 'Escaneo de tickets por foto y galería' },
  { icon: Landmark, text: 'Conecta tu banco automáticamente (Open Banking)' },
  { icon: BarChart3, text: 'Gráficas de ingresos, gastos y flujo mensual' },
  { icon: Target, text: 'Metas de ahorro con racha gamificada y niveles' },
  { icon: Trophy, text: 'Muro de las Peores Compras — aprende de los errores' },
  { icon: ShieldCheck, text: 'Mis Pólizas + Fondo de Emergencia + Blindaje Financiero' },
  { icon: Sparkles, text: 'Insight del Día + Diagnóstico Sin Filtros (0-100)' }
];

const PERSONAL_FEATURES = [
  { icon: MessageCircle, text: 'Asistente Quitamelo (chat con IA)' },
  { icon: Camera, text: 'Escaneo de tickets por foto' },
  { icon: BarChart3, text: 'Gráficas y estadísticas en vivo' },
  { icon: Target, text: 'Metas de ahorro + racha gamificada' },
  { icon: Trophy, text: 'Muro de las Peores Compras' },
  { icon: ShieldCheck, text: 'Mis Pólizas + Fondo de Emergencia' },
  { icon: Landmark, text: 'Conecta tu banco (Open Banking)' },
  { icon: Sparkles, text: 'Insight del Día + Diagnóstico' }
];

const ADVISOR_FEATURES = [
  { icon: CheckCircle2, text: 'Todo lo del Plan Personal' },
  { icon: ShieldCheck, text: 'Cédula CNSF verificada en tu perfil' },
  { icon: BarChart3, text: 'Panel de Estadísticas de clientes' },
  { icon: Link2, text: 'Link de invitación personal' },
  { icon: Bell, text: 'Notificaciones de leads en tiempo real' },
  { icon: FileText, text: 'Importar Estados de Cuenta (PDF)' },
  { icon: Zap, text: 'Insurance Market — recibe referidos' }
];

const AppShowcaseSection = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', plan_interest: 'personal' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (e) => {
    const value = e.target ? e.target.value : e;
    setForm((p) => ({ ...p, [field]: value }));
  };

  const submit = useCallback(async (e) => {
    e.preventDefault();
    const phoneDigits = (form.phone || '').replace(/\D/g, '');
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error('Por favor ingresa tu nombre');
      return;
    }
    if (phoneDigits.length !== 10) {
      toast.error('Ingresa un teléfono de 10 dígitos (México)');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error('Ingresa un email válido');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/app-waitlist`, {
        name: form.name.trim(),
        phone: phoneDigits,
        email: form.email.trim(),
        plan_interest: form.plan_interest
      });
      analytics.leadCapture(`app_waitlist_${form.plan_interest}`);
      setSubmitted(true);
      toast.success(
        `¡Estás dentro, ${form.name.split(' ')[0]}! 🚀`,
        {
          description: 'Serás de los primeros en probar la app. Te avisaremos apenas esté lista.',
          duration: 8000
        }
      );
    } catch (err) {
      toast.error('Hubo un error. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [form]);

  return (
    <section className="section app-showcase-section" id="conoce-la-app" data-testid="app-showcase-section">
      <div className="app-showcase-container">
        <div className="app-showcase-header">
          <span className="app-showcase-badge">
            <Sparkles className="w-4 h-4" />
            <span>Nuevo producto · Beta cerrada</span>
          </span>
          <h2 className="app-showcase-title">
            Conoce <span className="app-brand-neon">Quítamelo</span>, la app
          </h2>
          <p className="app-showcase-tagline">&ldquo;El App que te dice lo que tu cartera calla 😏&rdquo;</p>
          <p className="app-showcase-subtitle">
            Ahorro, control de gastos, escaneo de tickets, gráficas, Open Banking, chat con IA y hasta
            tus pólizas — todo en un solo lugar. El primer mes es gratis.
          </p>
        </div>

        <div className="app-showcase-grid">
          {/* Single phone image */}
          <div className="app-phone-wrap" data-testid="app-phone-mockup">
            <div className="app-phone-frame">
              <div className="app-phone-notch" aria-hidden="true" />
              <div className="app-phone-screen">
                <img
                  src={HERO_SCREEN.src}
                  alt={HERO_SCREEN.alt}
                  className="app-phone-image"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          {/* Right column: highlights list + pricing + waitlist */}
          <div className="app-showcase-right">
            {/* Highlights as a clean bulleted list */}
            <ul className="app-highlights-list" data-testid="app-highlights-list">
              {APP_HIGHLIGHTS.map((h, i) => (
                <li key={i} className="app-highlight-item">
                  <h.icon className="app-highlight-icon" />
                  <span>{h.text}</span>
                </li>
              ))}
            </ul>

            {/* Pricing — compact side-by-side */}
            <div className="app-plans app-plans-compact">
              <div className="app-plan app-plan-personal" data-testid="app-plan-personal">
                <div className="app-plan-header">
                  <span className="app-plan-icon">👤</span>
                  <div>
                    <h3 className="app-plan-name">Plan Personal</h3>
                    <p className="app-plan-target">Para ti y tu dinero</p>
                  </div>
                </div>
                <div className="app-plan-price">
                  <span className="app-plan-price-amount">$89</span>
                  <span className="app-plan-price-per">/mes</span>
                </div>
                <span className="app-plan-freemonth">🎁 Primer mes gratis</span>
              </div>

              <div className="app-plan app-plan-advisor" data-testid="app-plan-advisor">
                <span className="app-plan-badge-pro">PRO</span>
                <div className="app-plan-header">
                  <span className="app-plan-icon">💼</span>
                  <div>
                    <h3 className="app-plan-name">Plan Asesor</h3>
                    <p className="app-plan-target">Para agentes CNSF</p>
                  </div>
                </div>
                <div className="app-plan-price">
                  <span className="app-plan-price-amount">$149</span>
                  <span className="app-plan-price-per">/mes</span>
                </div>
                <span className="app-plan-freemonth">🎁 Primer mes gratis</span>
                <p className="app-plan-note">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Todo lo del Personal + Cédula CNSF verificada, Panel de clientes, Link de invitación, Notificaciones de leads e Insurance Market</span>
                </p>
              </div>
            </div>

            {/* Waitlist form */}
            <div className="app-waitlist" id="app-waitlist-form">
              <h3 className="app-waitlist-title">🚀 Únete a la beta cerrada</h3>
              <p className="app-waitlist-subtitle">
                La app aún no está en las tiendas. Regístrate y serás de los primeros en probarla
                cuando lancemos.
              </p>
              {submitted ? (
                <div className="app-waitlist-success" data-testid="app-waitlist-success">
                  ✅ ¡Listo! Te avisaremos apenas la beta esté disponible.
                </div>
              ) : (
                <form onSubmit={submit} className="app-waitlist-form" data-testid="app-waitlist-form-el">
                  <Input
                    type="text"
                    placeholder="Nombre"
                    autoComplete="name"
                    value={form.name}
                    onChange={handleChange('name')}
                    className="app-waitlist-input"
                    data-testid="app-waitlist-name"
                    required
                  />
                  <Input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder="Teléfono (10 dígitos)"
                    value={form.phone}
                    onChange={handleChange('phone')}
                    className="app-waitlist-input"
                    data-testid="app-waitlist-phone"
                    required
                  />
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="tu@email.com"
                    value={form.email}
                    onChange={handleChange('email')}
                    className="app-waitlist-input"
                    data-testid="app-waitlist-email"
                    required
                  />
                  <div className="app-waitlist-plan-choice">
                    <span>Plan de interés:</span>
                    <div className="app-waitlist-plan-buttons">
                      <button
                        type="button"
                        className={`app-plan-choice ${form.plan_interest === 'personal' ? 'is-active' : ''}`}
                        onClick={() => setForm((p) => ({ ...p, plan_interest: 'personal' }))}
                        data-testid="app-waitlist-plan-personal"
                      >
                        Personal · $89
                      </button>
                      <button
                        type="button"
                        className={`app-plan-choice app-plan-choice-pro ${form.plan_interest === 'advisor' ? 'is-active' : ''}`}
                        onClick={() => setForm((p) => ({ ...p, plan_interest: 'advisor' }))}
                        data-testid="app-waitlist-plan-advisor"
                      >
                        Asesor PRO · $149
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="app-waitlist-submit"
                    data-testid="app-waitlist-submit"
                  >
                    {loading ? 'Enviando…' : '🚀 Reservar mi lugar'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppShowcaseSection;
